import { ChipOption, useApiClient, useFetchInfiniteES } from '@marageti/z4-lib';
import {
  SearchResult,
  FilterConstraint,
  FilterOp,
} from '@marageti/z4-sdk/lib/entity';
import { Traveler, TravelerTags } from '@marageti/z4-sdk/lib/people';
import { useMemo } from 'react';

export const tagOptions: ChipOption[] = [
  { key: 0, label: 'VIP', value: TravelerTags.VIP },
  { key: 1, label: 'RPT', value: 'REPEAT_TRAVELER' },
  { key: 2, label: 'FOBY', value: TravelerTags.FOBY },
  { key: 3, label: 'BAD', value: TravelerTags.BAD_APPLE },
  { key: 4, label: 'FLAG', value: TravelerTags.RED_FLAG },
  { key: 5, label: 'AGENT', value: TravelerTags.TRAVEL_AGENT },
  { key: 6, label: 'JUNK', value: TravelerTags.JUNK },
];

const tagFilters: { [key: string]: FilterConstraint[] } = {
  [TravelerTags.VIP]: [{ field: 'tags', value: [TravelerTags.VIP] }],
  REPEAT_TRAVELER: [{ field: 'totalZicassoTrips', op: FilterOp.FilterOpGT, value: ['0'] }],
  [TravelerTags.FOBY]: [{ field: 'tags', value: [TravelerTags.FOBY] }],
  [TravelerTags.BAD_APPLE]: [{ field: 'tags', value: [TravelerTags.BAD_APPLE] }],
  [TravelerTags.RED_FLAG]: [{ field: 'tags', value: [TravelerTags.RED_FLAG] }],
  [TravelerTags.TRAVEL_AGENT]: [{ field: 'tags', value: [TravelerTags.TRAVEL_AGENT] }],
  [TravelerTags.JUNK]: [{ field: 'tags', value: [TravelerTags.JUNK] }],
};

interface PossibleSearchFields {
  countryOfResidence?: string[];
  fullText?: string;
  order?: string;
  sort?: string;
  status?: string[];
  tags?: string[];
}

const useTravelersSearch = ({
  countryOfResidence = [],
  fullText = '',
  order = '',
  sort = '',
  status = [],
  tags = [],
}: PossibleSearchFields) => {
  const apiClient = useApiClient();

  const getSearchRequest = () => {
    const filterBy: FilterConstraint[] = [];

    if (status.length) {
      filterBy.push({ field: 'userInfo.status', value: [...status] });
    }

    if (tags.length) {
      // Tags must be added individually and done as an 'OR' operation since we
      // have items in the tags chips list that are not actually tags
      tags.forEach((tag) => {
        filterBy.push(...tagFilters[tag]);
      });
    }

    if (countryOfResidence.length) {
      filterBy.push({ field: 'countryOfResidence', value: countryOfResidence });
    }

    return {
      filterBy,
      fullText: fullText.length > 2 ? fullText : undefined,
      phrase: fullText.includes('@'),
      pagination: { limit: 30, page: 1 },
      ...(sort && order) && { sortBy: [{ field: sort, order }] },
    };
  };

  // Fetch and exicute search request
  const searchRequest = getSearchRequest();

  const {
    data,
    error,
    hasMore,
    isLoading,
    loadMore,
  } = useFetchInfiniteES<Traveler>(apiClient.peopleClient.postTravelerSearch, searchRequest, { revalidateOnFocus: false });

  const travelers = useMemo(() => (
    data?.flatMap((searchResult: SearchResult<Traveler>) => searchResult.hits)
  ), [data]);

  return {
    travelers,
    error,
    hasMore,
    isLoading,
    loadMore,
    totalHits: data?.[0]?.totalHits || 0,
  };
};

export default useTravelersSearch;
